import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { selectDefaultFolder } from 'owa-mail-folder-forest-actions';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import {
    isMyOutlookFullPageActive,
    lazyDismissMyOutlookFullPage,
    lazyOpenMyOutlookFullPage,
} from 'owa-mail-folder-forest-view/lib/components/LazyMyOutlookSection';
import { getMyOutlookContextKey } from 'owa-personal-os-store/lib/selectors/getMyOutlookProductState';
import {
    isMyOutlookProductEnabled,
    isMyOutlookProductFlightEnabled,
} from 'owa-personal-os-store/lib/utils/isMyOutlookProductEnabled';
import { lazyReadMyOutlookEligibility } from 'owa-personal-os-store/lib/lazyModule';
import { setMyOutlookFullPageShowingMutator } from 'owa-personal-os-store/lib/mutators/setShowingInReadingPane';
import { trace } from 'owa-trace';
import { OPTION_ROUTE_KEYWORD } from 'owa-options-router/lib/routing/optionRoutes';
import { mutatorAction } from 'satcheljs';
import { createStore } from 'owa-satcheljs';

let pendingNavigation: AbortController | undefined;
let isPendingNavigationCurrent: (() => boolean) | undefined;
const getTodayRouteStore = createStore<{ pending: boolean }>('todayMailRoute', { pending: false });
const setTodayRoutePendingMutator = mutatorAction('setTodayRoutePending', (pending: boolean) => {
    getTodayRouteStore().pending = pending;
});

export function isTodayRoutePending(): boolean {
    return getTodayRouteStore().pending && !!isPendingNavigationCurrent?.();
}

export async function mailTodayRouteHandler(): Promise<void> {
    pendingNavigation?.abort();
    const controller = new AbortController();
    pendingNavigation = controller;
    const mailboxInfo = getGlobalSettingsAccountMailboxInfo();
    const contextKey = getMyOutlookContextKey(mailboxInfo);
    const selectedNode = getSelectedNode();
    const isCurrent = () =>
        !controller.signal.aborted &&
        getSelectedNode() === selectedNode &&
        getMyOutlookContextKey(mailboxInfo) === contextKey &&
        getMyOutlookContextKey(getGlobalSettingsAccountMailboxInfo()) === contextKey;

    if (!isMyOutlookProductFlightEnabled(mailboxInfo)) {
        setTodayRoutePendingMutator(false);
        selectDefaultFolder('Route');
        return;
    }

    isPendingNavigationCurrent = isCurrent;
    setTodayRoutePendingMutator(true);
    try {
        if (!isMyOutlookProductEnabled(mailboxInfo)) {
            await lazyReadMyOutlookEligibility.importAndExecute(mailboxInfo);
        }
        if (!isCurrent()) {
            return;
        }
        if (!isMyOutlookProductEnabled(mailboxInfo)) {
            selectDefaultFolder('Route');
            return;
        }

        const open = await lazyOpenMyOutlookFullPage.import();
        if (isCurrent()) {
            if (isMyOutlookProductEnabled(mailboxInfo)) {
                const outcome = await open(mailboxInfo, controller.signal);
                if (outcome === 'failed' && isCurrent()) {
                    selectDefaultFolder('Route');
                }
            } else {
                selectDefaultFolder('Route');
            }
        }
    } catch {
        trace.warn('TodayRouteNavigationFailed');
        if (isCurrent()) {
            selectDefaultFolder('Route');
        }
    } finally {
        if (pendingNavigation === controller) {
            setTodayRoutePendingMutator(false);
        }
    }
}

export async function mailTodayCleanupRouteHandler(
    _oldRoute?: string[],
    _oldParameters?: unknown,
    newRoute?: string[]
): Promise<boolean> {
    pendingNavigation?.abort();
    pendingNavigation = undefined;
    setTodayRoutePendingMutator(false);
    if (isMyOutlookFullPageActive()) {
        try {
            if (newRoute?.includes(OPTION_ROUTE_KEYWORD) || newRoute?.includes('settings')) {
                await lazyDismissMyOutlookFullPage.importAndExecute(true);
            } else {
                await lazyDismissMyOutlookFullPage.importAndExecute();
            }
        } catch {
            trace.warn('TodayRouteDismissFailed');
            setMyOutlookFullPageShowingMutator(false);
        }
    }
    return false;
}
